import { identifyCueboardPostHogUser } from '@/utils/posthog';
import { createPrefixedRandomID } from '@/utils/random-id';

export type AuthUser = {
  id: string;
  email: string;
  display_name?: string;
  avatar_url?: string;
  created_at?: string;
};

export type WebsiteCreditsPlanEntry = {
  grant_id?: string | null;
  plan_code: string;
  total_credits: number;
  remaining_credits: number;
  expires_at?: string | null;
};

export type WebsiteCredits = {
  available_credits: number;
  available_display_credits?: number;
  credit_display_divisor?: number;
  next_reset_at?: string | null;
  plan_code?: string | null;
  sub_status?: string | null;
  sub_created_at?: string | null;
  sub_current_period_start?: string | null;
  sub_current_period_end?: string | null;
  sub_cancel_at?: string | null;
  plans?: WebsiteCreditsPlanEntry[];
};

export type WebsiteCreditBalance = {
  available_credits: number;
  available_display_credits: number;
  credit_display_divisor: number;
  next_reset_at?: string | null;
  snapshot_at: string;
  maintenance_status: string;
};

export type WebsiteBillingPlan = {
  plan_code: string;
  type: string;
  credits_grant: number;
  period: string;
  currency: string;
  amount_cents: number;
  display_copy?: string | null;
};

export type WebsiteBillingPlansResponse = {
  plans: WebsiteBillingPlan[];
};

export type WebsiteCheckoutMode = 'subscription' | 'payment';

export type WebsiteCheckoutSessionRequest = {
  lookup_key: string;
  mode: WebsiteCheckoutMode;
  success_url: string;
  cancel_url: string;
};

export type WebsiteCheckoutSessionResponse = {
  checkout_url: string;
};

export type WebsitePortalSessionResponse = {
  url: string;
};

export type WebsiteRedeemResponse = {
  credits_granted: number;
  plan_code: string;
  expires_at?: string | null;
  balance_after: number;
};

type AuthRequestError = Error & {
  status?: number;
  requestId?: string;
};

function toAuthErrorSentence(message: string): string {
  const t = message.trim();
  if (!t) {
    return t;
  }
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function errorCodeToSentence(code: string): string {
  const known: Record<string, string> = {
    invalid_verification_code: 'Please enter a valid verification code.',
  };
  if (known[code]) {
    return known[code];
  }
  return toAuthErrorSentence(code.replace(/_/g, ' '));
}

function parseAuthErrorBody(body: string, fallback: string): string {
  const trimmed = body.trim();
  if (!trimmed) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed !== null && typeof parsed === 'object') {
      const record = parsed as Record<string, unknown>;

      const topMessage = record['message'];
      if (typeof topMessage === 'string' && topMessage.trim()) {
        return toAuthErrorSentence(topMessage);
      }

      const nested = record['error'];
      if (nested !== null && typeof nested === 'object') {
        const err = nested as Record<string, unknown>;
        const code = err['code'];
        if (
          typeof code === 'string' &&
          code.trim() === 'invalid_verification_code'
        ) {
          return 'Please enter a valid verification code.';
        }
        const msg = err['message'];
        if (typeof msg === 'string' && msg.trim()) {
          return toAuthErrorSentence(msg);
        }
        if (typeof code === 'string' && code.trim()) {
          return errorCodeToSentence(code.trim());
        }
      }

      if (typeof nested === 'string' && nested.trim()) {
        return toAuthErrorSentence(nested);
      }
    }
  } catch {
    // not JSON — fall through
  }

  if (!trimmed.startsWith('{') && trimmed.length < 400) {
    return trimmed;
  }

  return fallback;
}

function createAuthError(
  status: number,
  fallback: string,
  body: string,
  requestId?: string | null
): AuthRequestError {
  const error = new Error(
    parseAuthErrorBody(body, fallback)
  ) as AuthRequestError;
  error.status = status;
  if (requestId) {
    error.requestId = requestId;
  }
  return error;
}

function makeRequestID() {
  return createPrefixedRandomID('req');
}

export async function sendWebsiteLoginEmail(params: {
  email: string;
  inviteCode?: string;
}): Promise<{ bizId: string }> {
  const inviteCode = params.inviteCode?.trim();
  const body: {
    email: string;
    platform: 'web';
    invite_code?: string;
  } = {
    email: params.email,
    platform: 'web',
  };

  if (inviteCode) {
    body.invite_code = inviteCode;
  }

  const response = await fetch('/v1/auth/login?force_code=1', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw createAuthError(
      response.status,
      'Failed to send login email',
      await response.text().catch(() => '')
    );
  }

  return response.json();
}

export async function verifyWebsiteLoginToken(
  bizId: string,
  code: string,
  options?: {
    inviteCode?: string;
  }
): Promise<AuthUser> {
  const inviteCode = options?.inviteCode?.trim();
  const body: {
    bizId: string;
    code: string;
    invite_code?: string;
  } = { bizId, code };

  if (inviteCode) {
    body.invite_code = inviteCode;
  }

  const response = await fetch('/v1/auth/verify', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw createAuthError(
      response.status,
      'Failed to verify login',
      await response.text().catch(() => '')
    );
  }

  const user = (await response.json()) as AuthUser;
  identifyCueboardPostHogUser(user, {
    source: 'web.auth.verify',
    surface: 'website',
    runtime: 'browser',
  });
  return user;
}

export async function getWebsiteProfile(): Promise<AuthUser> {
  const response = await fetch('/v1/user/profile', {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw createAuthError(
      response.status,
      'Failed to load profile',
      await response.text().catch(() => '')
    );
  }

  const user = (await response.json()) as AuthUser;
  identifyCueboardPostHogUser(user, {
    source: 'web.profile',
    surface: 'website',
    runtime: 'browser',
  });
  return user;
}

export async function getWebsiteCredits(): Promise<WebsiteCreditBalance> {
  const response = await fetch('/v1/user/credits', {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw createAuthError(
      response.status,
      'Failed to load usage',
      await response.text().catch(() => '')
    );
  }

  return response.json();
}

export async function refreshWebsiteCredits(): Promise<WebsiteCredits> {
  const response = await fetch('/v1/user/credits/refresh', {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    throw createAuthError(
      response.status,
      'Failed to refresh billing',
      await response.text().catch(() => '')
    );
  }

  return response.json();
}

export async function getWebsiteBillingSummary(): Promise<WebsiteCredits> {
  const response = await fetch('/v1/user/billing-summary', {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw createAuthError(
      response.status,
      'Failed to load billing summary',
      await response.text().catch(() => '')
    );
  }

  return response.json();
}

export async function getWebsiteBillingPlans(): Promise<WebsiteBillingPlansResponse> {
  const response = await fetch('/v1/user/plans', {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw createAuthError(
      response.status,
      'Failed to load billing plans',
      await response.text().catch(() => '')
    );
  }

  return response.json();
}

export async function createWebsiteCheckoutSession(
  input: WebsiteCheckoutSessionRequest
): Promise<WebsiteCheckoutSessionResponse> {
  const requestId = makeRequestID();
  const response = await fetch('/v1/user/billing/checkout', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Request-ID': requestId,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw createAuthError(
      response.status,
      'Failed to create checkout session',
      await response.text().catch(() => ''),
      response.headers.get('X-Request-ID') || requestId
    );
  }

  return response.json();
}

export async function createWebsiteBillingPortalSession(
  returnUrl: string
): Promise<WebsitePortalSessionResponse> {
  const response = await fetch('/v1/user/billing/portal', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      return_url: returnUrl,
    }),
  });

  if (!response.ok) {
    throw createAuthError(
      response.status,
      'Failed to open billing portal',
      await response.text().catch(() => '')
    );
  }

  return response.json();
}

export async function redeemWebsiteCode(
  code: string
): Promise<WebsiteRedeemResponse> {
  const response = await fetch('/v1/user/redeem', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
    }),
  });

  if (!response.ok) {
    throw createAuthError(
      response.status,
      'Failed to redeem code',
      await response.text().catch(() => '')
    );
  }

  return response.json();
}

export async function logoutWebsiteUser(): Promise<void> {
  const response = await fetch('/v1/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw createAuthError(
      response.status,
      'Failed to log out',
      await response.text().catch(() => '')
    );
  }
}
